import { useStaticQuery, graphql } from "gatsby"
import { useCallback } from "react"

import { DEFAULT_STOREFRONT } from "../../../cart/constants"
import { consolidateBundle, consolidateProduct } from "../../../utils/shop"

import { useGetBundleByCode } from "@hooks/bundles"
import { useGetProductBySku } from "@hooks/products"
import { useGetStoryblokBundle } from "@hooks/V2/storyblok/useBundle"

interface storyBlokProduct {
  uuid: string
  content: string
  full_slug: string
}

export const useGetStoryblokProduct = () => {
  const data = useStaticQuery(graphql`
    query StoryblokProducts {
      allStoryblokEntry(
        filter: { full_slug: { regex: "/settings/products/" } }
      ) {
        edges {
          node {
            uuid
            content
            full_slug
          }
        }
      }
    }
  `)

  const getStoryblokProduct = useCallback(
    (
      id: string,
      queryBy: "id" | "sku" | "slug" = "id"
    ): Storyblok.ProductConfig | null => {
      const product = data.allStoryblokEntry.edges.find(
        (product: { node: storyBlokProduct }) => {
          if (queryBy === "slug") {
            return product.node.full_slug === id
          } else if (queryBy === "sku") {
            const content = JSON.parse(product.node.content)
            return (
              content.sku === id && content.storefront === DEFAULT_STOREFRONT
            )
          } else {
            return product.node.uuid === id
          }
        }
      )

      if (product) {
        return {
          uuid: product.node.uuid,
          ...JSON.parse(product.node.content),
        }
      }

      return null
    },
    [data]
  )

  return getStoryblokProduct
}

export const useGetProductBySlug = (): ((
  slug: string
) => YocoCom.ConsolidatedCartItem | null) => {
  const getStoryBlokProduct = useGetStoryblokProduct()
  const getStoryBlokBundle = useGetStoryblokBundle()
  const getSdkProduct = useGetProductBySku()
  const getSdkBundle = useGetBundleByCode()

  const getProductBySlug = useCallback(
    (slug: string) => {
      if (slug.startsWith("settings/products/")) {
        const storyblokProduct = getStoryBlokProduct(slug, "slug")

        if (storyblokProduct) {
          const sdkProduct = getSdkProduct(
            storyblokProduct.sku,
            storyblokProduct.storefront
          )

          if (sdkProduct) {
            return consolidateProduct(
              sdkProduct.id.toString(),
              1,
              sdkProduct,
              storyblokProduct
            )
          }
        }
      }

      if (slug.startsWith("settings/bundles/")) {
        const storyblokBundle = getStoryBlokBundle(slug, "slug")

        if (storyblokBundle) {
          const sdkBundle = getSdkBundle(
            storyblokBundle.code,
            storyblokBundle.storefront
          )
          if (sdkBundle) {
            return consolidateBundle(
              sdkBundle.id.toString(),
              1,
              sdkBundle,
              storyblokBundle
            )
          }
        }
      }

      return null
    },
    [getSdkProduct, getStoryBlokProduct, getStoryBlokBundle, getSdkBundle]
  )

  return getProductBySlug
}
